'use client';

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import Message, { MessageRef } from './Message';

interface MessageData {
  id?: number;
  role: 0 | 1 | '0' | '1';
  content: string;
  timestamp?: string;
  isComplete?: boolean;
  notification_id?: number;
}

interface MessageListProps {
  messages: MessageData[];
  messageRefs: React.MutableRefObject<Map<number, MessageRef>>;
  onButtonClick?: (buttonText: string) => void;
}

// Memoized wrapper to create stable ref callbacks and prevent unnecessary re-renders
interface MessageWrapperProps {
  message: MessageData;
  isLast: boolean;
  onButtonClick: ((buttonText: string) => void) | undefined;
  messageRefs: React.MutableRefObject<Map<number, MessageRef>>;
}

const MemoizedMessageWrapper: React.FC<MessageWrapperProps> = React.memo(
  ({ message, isLast, onButtonClick, messageRefs }) => {
    // Create stable ref callback using useMemo
    const refCallback = useMemo(() => {
      if (!message.notification_id) return undefined;
      return (ref: MessageRef | null) => {
        if (messageRefs?.current) {
          if (ref) {
            messageRefs.current.set(message.notification_id!, ref);
          } else {
            messageRefs.current.delete(message.notification_id!);
          }
        }
      };
    }, [message.notification_id, messageRefs]);

    return (
      <Message
        {...(refCallback !== undefined && { ref: refCallback })}
        message={message}
        isLast={isLast}
        {...(onButtonClick !== undefined && { onButtonClick })}
      />
    );
  }
);
MemoizedMessageWrapper.displayName = 'MemoizedMessageWrapper';

const MessageList: React.FC<MessageListProps> = ({
  messages,
  messageRefs,
  onButtonClick,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);
  const lastContentLengthRef = useRef(0);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  // Initial scroll when component mounts
  useEffect(() => {
    scrollToBottom('instant');
  }, [scrollToBottom]);

  // Scroll on new messages or content changes
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const lastMessageContent = lastMessage?.content || '';

    // Compare message count AND content length separately
    if (
      messages.length > 0 &&
      (messages.length !== lastMessageCountRef.current ||
        lastMessageContent.length !== lastContentLengthRef.current)
    ) {
      scrollToBottom();
      lastMessageCountRef.current = messages.length;
      lastContentLengthRef.current = lastMessageContent.length;
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const currentRefs = messageRefs?.current;
    return () => {
      if (currentRefs) {
        currentRefs.clear();
      }
    };
  }, [messageRefs]);

  return (
    <div
      className="overflow-y-auto py-4"
      ref={scrollContainerRef}
      style={{ scrollbarWidth: 'thin' }}
      role="log"
      aria-label="Historia wiadomosci"
      aria-live="polite"
      aria-relevant="additions"
    >
      {/* Regular chat messages */}
      {messages.map((message, index) => (
        <MemoizedMessageWrapper
          key={message.id || `message-${message.timestamp}-${index}`}
          message={message}
          isLast={index === messages.length - 1}
          onButtonClick={onButtonClick}
          messageRefs={messageRefs}
        />
      ))}

      {/* Invisible element for scrolling */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default React.memo(MessageList);
